import LegalPageLayout from '@/components/layout/LegalPageLayout';

const tocItems = [
  { id: 'overview', label: 'Overview' },
  { id: 'getting-started', label: 'Getting Started' },
  { id: 'account', label: 'Account Setup' },
  { id: 'booking', label: 'Booking & Scheduling' },
  { id: 'team', label: 'Team Management' },
  { id: 'billing', label: 'Billing & Plans' },
  { id: 'troubleshooting', label: 'Troubleshooting' },
  { id: 'contact', label: 'Contact Support' },
];

function Section({ id, title, children }) {
  return (
    <section id={id} className="reveal py-10 border-b border-white/[0.04]">
      <h2 className="text-[22px] font-semibold tracking-tight leading-snug mb-5 text-white scroll-mt-[110px]">
        {title}
      </h2>
      <div className="text-sm text-zinc-500 leading-[1.75]">{children}</div>
    </section>
  );
}

function InfoCard({ children, accent }) {
  const border = accent ? 'border-white/[0.12]' : 'border-white/[0.06]';
  const bg = accent ? 'bg-white/[0.04]' : 'bg-white/[0.02]';
  return (
    <div className={`rounded-2xl border ${border} ${bg} p-5 my-5 transition-colors hover:border-white/[0.12]`}>
      {children}
    </div>
  );
}

export default function HelpCenter() {
  return (
    <LegalPageLayout
      title="Help Center"
      subtitle="Everything you need to get the most out of ScheduleX. Search topics or browse by category."
      tocItems={tocItems}
    >
      <Section id="overview" title="Overview">
        <p className="mb-4">
          Welcome to the ScheduleX Help Center. Here you will find guides, tutorials, and answers to common questions about using our scheduling platform.
        </p>
        <InfoCard accent>
          <p className="font-medium text-white mb-2">Quick Links</p>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { t: 'Create a Calendar', s: 'getting-started' },
              { t: 'Invite Team Members', s: 'team' },
              { t: 'Manage Billing', s: 'billing' },
            ].map(l => (
              <button key={l.s} onClick={() => document.getElementById(l.s)?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-left px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06] text-xs text-zinc-400 hover:text-white hover:border-white/[0.12] transition-all">
                <span className="font-medium">{l.t}</span>
              </button>
            ))}
          </div>
        </InfoCard>
      </Section>

      <Section id="getting-started" title="Getting Started">
        <p className="mb-4">Follow these steps to set up your ScheduleX account and create your first booking calendar.</p>
        <InfoCard>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-7 h-7 rounded-lg bg-white/[0.06] flex items-center justify-center text-xs font-bold text-white">1</div>
            <p className="font-medium text-white">Sign up for an account</p>
          </div>
          <p className="mb-4 pl-10">Visit the registration page and create your account with your email and a secure password. You will be prompted to create an organization during signup.</p>
        </InfoCard>
        <InfoCard>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-7 h-7 rounded-lg bg-white/[0.06] flex items-center justify-center text-xs font-bold text-white">2</div>
            <p className="font-medium text-white">Create your first calendar</p>
          </div>
          <p className="mb-4 pl-10">Go to the Calendars section and click &quot;New Calendar.&quot; Set your availability, duration, and buffer times. You can customize the booking page URL.</p>
        </InfoCard>
        <InfoCard>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-7 h-7 rounded-lg bg-white/[0.06] flex items-center justify-center text-xs font-bold text-white">3</div>
            <p className="font-medium text-white">Share your booking link</p>
          </div>
          <p className="pl-10">Copy your public booking URL and share it with clients, colleagues, or embed it on your website. Anyone with the link can book a time with you.</p>
        </InfoCard>
      </Section>

      <Section id="account" title="Account Setup">
        <p className="mb-4">Configure your profile and organization settings to personalize your ScheduleX experience.</p>
        <InfoCard>
          <p className="font-medium text-white mb-2">Profile Settings</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" /><span>Update your name, email, and timezone in the Profile section</span></li>
            <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" /><span>Upload a profile picture and organization logo</span></li>
            <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" /><span>Set your default working hours and availability</span></li>
          </ul>
        </InfoCard>
        <InfoCard>
          <p className="font-medium text-white mb-2">Security</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" /><span>Enable two-factor authentication for extra protection</span></li>
            <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" /><span>Review active sessions and revoke unused ones</span></li>
            <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" /><span>Change your password regularly</span></li>
          </ul>
        </InfoCard>
      </Section>

      <Section id="booking" title="Booking & Scheduling">
        <p className="mb-4">Learn how to manage bookings, set availability, and customize your scheduling experience.</p>
        <InfoCard>
          <p className="font-medium text-white mb-2">Availability Rules</p>
          <p>Set your working hours, add buffer time between meetings, and create date-specific overrides for holidays or time off. You can set different availability for different calendars.</p>
        </InfoCard>
        <InfoCard>
          <p className="font-medium text-white mb-2">Custom Fields</p>
          <p>Collect additional information from bookers by adding custom fields to your booking form. Ask for phone numbers, meeting topics, or any other details you need before the meeting.</p>
        </InfoCard>
        <InfoCard>
          <p className="font-medium text-white mb-2">Notifications</p>
          <p>Get instant email notifications when someone books, reschedules, or cancels an appointment. You can also set up reminder emails for yourself and your guests before meetings.</p>
        </InfoCard>
      </Section>

      <Section id="team" title="Team Management">
        <p className="mb-4">Invite team members, assign roles, and manage your organization from one place.</p>
        <InfoCard>
          <p className="font-medium text-white mb-2">Roles & Permissions</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" /><span><strong className="text-zinc-400">Owner</strong> — Full control over the organization and all settings</span></li>
            <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" /><span><strong className="text-zinc-400">Admin</strong> — Can manage calendars, team members, and billing</span></li>
            <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" /><span><strong className="text-zinc-400">Member</strong> — Can manage their own calendars and bookings</span></li>
          </ul>
        </InfoCard>
      </Section>

      <Section id="billing" title="Billing & Plans">
        <p className="mb-4">Manage your subscription, upgrade plans, and handle payment details.</p>
        <InfoCard>
          <p className="font-medium text-white mb-2">Plan Comparison</p>
          <div className="grid grid-cols-3 gap-2 text-xs text-zinc-500">
            <div className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06]"><span className="font-medium text-white block">Free</span>1 calendar</div>
            <div className="px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.1]"><span className="font-medium text-white block">Pro</span>5 calendars</div>
            <div className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06]"><span className="font-medium text-white block">Enterprise</span>Unlimited</div>
          </div>
        </InfoCard>
        <InfoCard accent>
          <p className="font-medium text-white mb-1">Cancel Anytime</p>
          <p>You can cancel your subscription at any time from the Billing page. Your access continues until the end of your current billing period.</p>
        </InfoCard>
      </Section>

      <Section id="troubleshooting" title="Troubleshooting">
        <p className="mb-4">Common issues and quick fixes.</p>
        <InfoCard>
          <p className="font-medium text-white mb-2">Calendar not showing available slots</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" /><span>Check that your availability hours are set correctly</span></li>
            <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" /><span>Ensure your timezone is set accurately in Profile settings</span></li>
            <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" /><span>Look for date overrides that may be blocking all slots</span></li>
          </ul>
        </InfoCard>
        <InfoCard>
          <p className="font-medium text-white mb-2">Emails not being received</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" /><span>Check your spam or junk folder</span></li>
            <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" /><span>Verify your email address in Profile settings</span></li>
            <li className="flex items-start gap-3"><span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" /><span>Add noreply@schedulex.site to your contacts</span></li>
          </ul>
        </InfoCard>
      </Section>

      <Section id="contact" title="Contact Support">
        <p className="mb-4">
          Can not find what you are looking for? Our support team is here to help.
        </p>
        <InfoCard accent>
          <p className="font-medium text-white mb-1">Email Support</p>
          <p>Reach us at <a href="mailto:support@schedulex.site" className="text-white hover:underline">support@schedulex.site</a>. We typically respond within 24 hours on business days.</p>
        </InfoCard>
        <InfoCard>
          <p className="font-medium text-white mb-1">Status Page</p>
          <p>Check <a href="/status" className="text-white hover:underline">schedulex.site/status</a> for real-time system status and any ongoing incidents.</p>
        </InfoCard>
      </Section>
    </LegalPageLayout>
  );
}
